import * as React from 'react';
import moment from 'moment';
import { styled } from '@mui/material';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { useAppDispatch, useAppSelector } from "../../redux";
import {
    expandCompetition,
    getFixtures,
} from "../../redux/slices/fixtures";
import { formatGameMinute } from "../../utils";
import { useVirtualizer } from '@tanstack/react-virtual'; // Update the import here

import './index.scss';
import PullToRefresh from "react-simple-pull-to-refresh";
import {useNavigate} from "react-router-dom";

interface ExpandMoreProps extends IconButtonProps {
    expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

export default function Fixtures(props: any) {
    const browserTimezone = moment.tz.guess();
    const [prevProps, setPrevProps] = React.useState(props);
    const { activeGame } = useAppSelector(state => state.fixtures);
    const activeGameId = activeGame?.id;

    const dispatch = useAppDispatch();
    const navigate = useNavigate(); // Initialize the navigate function

    React.useEffect(() => {
        setTimeout(() => setPrevProps(props), 5000)
    }, [props]);

    const previousPropsGame = (competition: any, game: any) => prevProps.competitions?.filter((c: any) => c.id === competition.id)[0]?.games.filter((g: any) => g.id === game.id)[0]
    const renderGoalSVG = () => <svg
        className="sr-momentum__event-icon " viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        style={{width: '10px', marginLeft: '10px'}}
    >
        <path
            d="M22.66 15.35l-1.4-.46c-.1-.05-.2-.06-.3-.06-.23 0-.43.1-.58.33L17.76 19c-.2.23-.2.6 0 .84l.8 1.2c-1.9 1.4-4.03 2.1-6.4 2.14v-1.63c-.05-.4-.23-.64-.57-.74l-4.43-1.3c-.1-.02-.18-.04-.26-.04-.23 0-.4.1-.55.3L5.38 21c-.57-.45-1-.82-1.25-1.08-1.22-1.2-2.12-2.6-2.67-4.24l1.4-.57c.3-.1.47-.33.47-.7V9.76c0-.32-.23-.6-.54-.7L1.37 8.6c.7-2.25 1.98-4.1 3.9-5.52l1 1.25c.18.2.35.3.58.3.1 0 .17 0 .25-.03l4.43-1.4c.37-.12.55-.38.54-.74L12.05.82c2.3 0 4.4.68 6.36 2.03l-.95 1.48c-.1.14-.14.27-.14.4 0 .17.07.3.18.46l2.7 3.74c.15.22.33.34.56.34.1 0 .2-.03.3-.06l1.62-.57c.33 1 .5 2.1.5 3.25 0 1.24-.17 2.38-.5 3.45zm.36-8.1c-.65-1.46-1.5-2.72-2.56-3.78-1.07-1.07-2.34-1.9-3.82-2.52-1.47-.63-3-.95-4.62-.95h-.14C10.25 0 8.7.34 7.23.98c-1.47.65-2.74 1.52-3.8 2.6-1.07 1.08-1.9 2.37-2.5 3.85C.3 8.9 0 10.43 0 12.05v.07c0 1.6.33 3.16.97 4.63.64 1.46 1.5 2.72 2.57 3.78 1.08 1.07 2.35 1.9 3.82 2.52 1.48.63 3.02.95 4.63.95h.12c1.63 0 3.18-.34 4.66-.98 1.5-.65 2.74-1.52 3.8-2.6 1.07-1.1 1.9-2.36 2.5-3.85.6-1.46.92-2.97.92-4.56v-.12c0-1.6-.34-3.16-.98-4.62zm-9.2.66c-.16-.2-.34-.3-.57-.3-.1 0-.2.02-.28.06L8.7 9.06c-.35.1-.5.36-.5.73l.05 4.5c0 .32.2.62.5.7l4.22 1.34c.1.04.2.05.28.05.23 0 .42-.1.57-.3l2.72-3.7c.1-.15.15-.32.15-.47 0-.14-.07-.3-.16-.45L13.82 7.9"
        ></path>
    </svg>

    const parentRef = React.useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: props.competitions.length,
        getScrollElement: () => parentRef.current,
        measureElement: (el) => el.getBoundingClientRect().height, // Measure the height of each item dynamically
        estimateSize: () => 100, // A rough estimate; the virtualizer will correct this after measuring
        overscan: 5,
    });

    const handleExpandClick = (competitionId: bigint) => {
        dispatch(expandCompetition(competitionId));
    };

    const onClickGame = async (event) => {
        navigate(`/football/match/${event.id}`);
    };

    const handleRefresh = async () => {
        await dispatch(getFixtures())
    }

    function renderGameStatus(game: any) {
        if (['Coverage canceled', 'Ended'].includes(game.status.description)) {
            return 'Ended'
        }

        return game.status.description
    }

    return (
        <PullToRefresh onRefresh={handleRefresh}>
            <div className="fixtures-component-wrapper" ref={parentRef}>
                {
                    props.competitions.length === 0 &&
                    <div className="no-available-games">
                        <EventBusyIcon/>
                        <h4>No games available at this moment.</h4>
                        <p>Please check again later.</p>
                    </div>
                }

                <div>
                    <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                        {rowVirtualizer.getVirtualItems().map(virtualRow => {
                            const competition = props.competitions[virtualRow.index];
                            return (
                                <div
                                    key={competition.id}
                                    data-index={virtualRow.index} //needed for dynamic row height measurement
                                    className="fixtures"
                                    ref={node => rowVirtualizer.measureElement(node)} //measure dynamic row height
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        transform: `translateY(${virtualRow.start + 5}px)`,
                                    }}
                                >
                                    <div className='competition card-shadow' onClick={() => handleExpandClick(competition.id)}>
                                        <div className='left-wrapper'>
                                            <div className="logo" style={{
                                                backgroundImage: `url(${
                                                    competition.uniqueTournament?.image || `https://api.sofascore.app/api/v1/unique-tournament/${competition.uniqueTournament?.id}/image`
                                                })`
                                            }}></div>
                                            <div className='name'>{competition.category.name}: {competition.name}</div>
                                        </div>
                                        <div className='right-wrapper'>
                                            <div>{competition.games.length}</div>
                                            <ExpandMore
                                                expand={competition.expanded}
                                                aria-expanded={competition.expanded}
                                                aria-label="show more"
                                                className='show-more-button'
                                            >
                                                <ExpandMoreIcon />
                                            </ExpandMore>
                                        </div>
                                    </div>
                                    <Collapse in={competition.expanded} timeout="auto" unmountOnExit>
                                        <div className='games'>
                                            {
                                            competition.games.map((game: any) => {
                                                const prevPropsGame = previousPropsGame(competition, game)

                                                return <div key={game.id} className={'game ' + (game.live ? 'game-live' : '')}
                                                            onClick={() => onClickGame(game)}>
                                                            <div className={"teams-info-wrapper" + (game.id === activeGameId ? ' active' : '')}>
                                                                <div className='teams-info'>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column'
                                                                    }}>
                                                                        <div className='team'>
                                                                            {game.homeScore?.hasOwnProperty('display') ?
                                                                                <span>{game.homeScore?.display}</span> :
                                                                                <span style={{color: 'transparent'}}>-</span>
                                                                            }

                                                                            <div className="logo" style={{ backgroundImage: `url(${game.homeTeam.image + '/small'})`}}></div>
                                                                    <div
                                                                        className='name'
                                                                        style={{fontWeight: game.live && (game.homeScore?.display > prevPropsGame?.awayScore?.display) ? '600' : '500'}}
                                                                    >{game.homeTeam.shortName}</div>
                                                                    {
                                                                        game.homeRedCards &&
                                                                        <svg
                                                                            className="sr-momentum__event-icon  srt-fill-soccer-red-card"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 16 24"
                                                                            style={{width: '16px', height: '16px', marginLeft: '5px'}}
                                                                        >
                                                                            <path
                                                                                d="M2 0h12c1 0 2 1 2 2v20c0 1-1 2-2 2H2c-1 0-2-1-2-2V2c0-1 1-2 2-2z"></path>
                                                                        </svg>
                                                                    }
                                                                    {
                                                                        game.live &&
                                                                        (game.homeScore?.display > prevPropsGame?.homeScore?.display) &&
                                                                        renderGoalSVG()
                                                                    }
                                                                        </div>
                                                                        <div className='team'>
                                                                            {game.awayScore?.hasOwnProperty('display') ?
                                                                        <span>{game.awayScore?.display}</span> : <span style={{color: 'transparent'}}>-</span>}
                                                                            <div className="logo" style={{
                                                                                backgroundImage: `url(${game.awayTeam.image + '/small'})`
                                                                            }}></div>
                                                                    <div
                                                                        className='name'
                                                                        style={{fontWeight: game.live && (game.awayScore?.display > prevPropsGame?.awayScore?.display) ? '500' : '400'}}
                                                                    >
                                                                        {game.awayTeam.shortName}

                                                                        {
                                                                            game.awayRedCards &&
                                                                            <svg
                                                                                className="sr-momentum__event-icon  srt-fill-soccer-red-card"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                viewBox="0 0 16 24"
                                                                                style={{width: '16px', height: '16px', marginLeft: '5px'}}
                                                                            >
                                                                                <path
                                                                                    d="M2 0h12c1 0 2 1 2 2v20c0 1-1 2-2 2H2c-1 0-2-1-2-2V2c0-1 1-2 2-2z"></path>
                                                                            </svg>
                                                                        }
                                                                    </div>
                                                                    {
                                                                        game.live &&
                                                                        (game.awayScore?.display > prevPropsGame?.awayScore?.display) &&
                                                                        renderGoalSVG()
                                                                    }
                                                                        </div>
                                                                    </div>
                                                                    <div className="time-section-wrapper">
                                                                        {(game.settings.hasEventPlayerStatistics || game.settings.hasEventPlayerHeatMap) && (
                                                                            <div className="graph-available">
                                                                                <EqualizerIcon/>
                                                                            </div>
                                                                        )}
                                                                        {game.live ? (
                                                                            <div className='time-info-live'>
                                                                                {game.status.description === 'Halftime' ? 'HT' : <>
                                                                                    {formatGameMinute(game)}<span>'</span>
                                                                                </>}
                                                                            </div>
                                                                        ) : (
                                                                            <div className='time-info-fixture'>
                                                                                <span>{moment.unix(game.startTimestamp).tz(browserTimezone).format('HH:mm')}</span>
                                                                                <div>{renderGameStatus(game)}</div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                            })
                                            }
                                        </div>
                                    </Collapse>
                                </div>)
                        })}
                    </div>
                </div>
            </div>
        </PullToRefresh>
    )
}
